// src/hooks/useAlertMutations.ts
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast'; // Use the custom hook
import { AlertPreviewDto, MonitorAlertsSearched } from '@/app/AlertsDashboard/types'; // Adjust path if needed - Assuming AlertListResponse is here

// Need to import InfiniteData type if not already available globally
import type { InfiniteData } from '@tanstack/react-query';
import axiosClient from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

interface AlertTookResponse {
  tookAlert: boolean;
}

// Function to make the API call
async function takeAlertApi(alertId: string): Promise<AlertTookResponse> {
  const response = await axiosClient.post<AlertTookResponse>(`/AlertMonitoring/${alertId}/take`);
  return response.data;
}

interface UseTakeAlertMutationOptions {
  onSuccessCallback?: (data: AlertTookResponse, alertId: string) => void;
  onErrorCallback?: (error: Error, alertId: string) => void; // Add error callback
}

export function useTakeAlertMutation({
  onSuccessCallback,
  onErrorCallback, // Destructure new callback
}: UseTakeAlertMutationOptions) {
  const queryClient = useQueryClient();
  const { toast } = useToast(); // Get toast function from the custom hook
  const navigate = useNavigate();
  return useMutation<AlertTookResponse, Error, string>({
    mutationFn: (alertId: string) => takeAlertApi(alertId), // Specify mutationFn *inside* options
    onSuccess: (data: AlertTookResponse, alertId: string) => {
      if (!data.tookAlert) {
        toast({
          title: 'Alert Taken',
          description: (
            <div>
              Alert has been taken by someone else.{' '}
              <Button variant={'outline'} size={'sm'} onClick={() => navigate(`/alert/${alertId}`)}>
                Open
              </Button>
            </div>
          ),
          duration: 1500,
        });
        // Remove the alert from the specific query cache
        const queryKey = ['alerts', 'new', 20, 'v2']; // Match the query key used in useAlertsV2
        queryClient.setQueryData<InfiniteData<{ alerts: AlertPreviewDto[] }> | undefined>(
          queryKey,
          (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                alerts: page.alerts?.filter((alert) => alert?.id !== alertId),
              })),
            };
          }
        );
      }
      onSuccessCallback?.(data, alertId);
    },
    onError: (error: Error, alertId: string) => {
      // Pass alertId to onError
      toast({
        title: 'Error',
        description: `Failed to take alert: ${error.message}`,
      });
      console.error('Take Alert Error:', error);
      onErrorCallback?.(error, alertId); // Call the passed-in error callback
    },
  });
}
